import React, { Component } from 'react';
import * as d3 from 'd3';
import * as _ from 'lodash';
import { withTheme } from 'styled-components';

import { KEYBOARD_CODES, overlayColors } from '../../../../../common/constants';
import { LineChart } from '../../../../../common/components/Chart/hoc/LineChart';
import { isElementPartiallyVisible } from '../../../../../common/utils';
import * as graphUtils from '../../../../../common/components/Chart/utils/graphUtils';
import { addOutlierRightClickEvent, cleanupRightClickEvent } from '../../../../../common/components/Chart/utils/rightClickUtils';
import AnnotationTooltip from '../../../../Annotations/components/AnnotationTooltip';
import {
  getAnnotationColor,
  getAnnotationIconGlyph,
  getAnnotationIconDataUrl,
  lightenHex
} from '../../../../Annotations/utils/annotationPresentation';
import { groupByProximity } from '../../../../Annotations/utils/clusterUtils';
import { isRangeAnnotation } from '../../../../Annotations/utils/annotationRange';

class TrendChart extends Component {
  constructor(props) {
    super(props);

    this.drawAlarmAndBaselineLines = this.drawAlarmAndBaselineLines.bind(this);
    this.drawOutliers = this.drawOutliers.bind(this);
    this.drawFdp = this.drawFdp.bind(this);
    this.drawStartTime = this.drawStartTime.bind(this);
    this.drawPoints = this.drawPoints.bind(this);
    this.scaleYaxisToShowAlarmLines = this.scaleYaxisToShowAlarmLines.bind(this);

    this.showAnnotationTooltip = this.showAnnotationTooltip.bind(this);
    this.hideAnnotationTooltip = this.hideAnnotationTooltip.bind(this);
    this.scheduleHideAnnotationTooltip = this.scheduleHideAnnotationTooltip.bind(this);
    this.drawClusterBubbleAtX = this.drawClusterBubbleAtX.bind(this);
    // Span-highlight on the plot, driven by the RangeLaneBand hover above it.
    this.highlightAnnotationRegion = this.highlightAnnotationRegion.bind(this);
    this.clearAnnotationHighlight = this.clearAnnotationHighlight.bind(this);

    this.state = {
      showAnnotationTooltip: false,
      tooltipAnnotation: null,
      tooltipPos: { x: 0, y: 0, placement: 'top', align: 'center' }
    };

    this.tooltipHideTimer = null;
  }

  // === Annotation helpers (color/icon consistent with overlay & tooltip) ===
  // Removed local type/icon/lighten helpers in favor of shared utilities

  drawAnnotationAtX(a, xVal, baseColor) {
    const { chartName } = this.props;
    // Point flags sit just above the plot (negative y, drawn via the SVG's
    // visible overflow), forming a flags row between the range-lane band and the
    // plot. The band reserves that gap with its bottom margin (RangeLaneBand),
    // so the flags clear both the band/×N badges and the data line.
    const iconY = -14;
    const color = baseColor;
    const hoverColor = lightenHex(color, 16);
    const neutralFill = (this.props.theme && this.props.theme.colors && this.props.theme.colors.greyXXXL) || '#F7F7F7';
    const iconSize = 14;
    const dataUrl = getAnnotationIconDataUrl(a.type, { size: iconSize, color });
    const glyphFallback = getAnnotationIconGlyph(a.type);

    const g = d3.select(this.ctx.chart)
      .append('g')
      .attr('class', `${chartName} annotationIcon`)
      .attr('transform', `translate(${xVal}, ${iconY})`)
      .attr('cursor', 'pointer');

    const circle = g.append('circle')
      .attr('r', 11)
      .attr('fill', neutralFill)
      .attr('stroke', lightenHex(color, 48))
      .attr('stroke-width', 1.5);

    if (dataUrl) {
      g.append('image')
        .attr('href', dataUrl)
        .attr('width', iconSize)
        .attr('height', iconSize)
        .attr('x', -iconSize / 2)
        .attr('y', -iconSize / 2)
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .attr('pointer-events', 'none');
    } else {
      g.append('text')
        .text(glyphFallback)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .attr('y', 0.5) // slight visual centering
        .attr('font-size', 10)
        .attr('fill', (this.props.theme && this.props.theme.colors && this.props.theme.colors.greyXXD) || '#999');
    }

    if (this.props.onAnnotationClick) {
      g.on('click', () => this.props.onAnnotationClick(a));
    }

    // Hover styles + tooltip
    g.on('mouseenter', () => {
      if (circle.setAttribute) { circle.setAttribute('stroke', lightenHex(color, 72)); } else { circle.attr('stroke', lightenHex(color, 72)); }
      this.showAnnotationTooltip(a, xVal);
    });
    g.on('mouseleave', () => {
      if (circle.setAttribute) { circle.setAttribute('stroke', lightenHex(color, 48)); } else { circle.attr('stroke', lightenHex(color, 48)); }
      this.scheduleHideAnnotationTooltip();
    });
  }

  // Simple cluster bubble (count) for dense groups
  drawClusterBubbleAtX(count, xVal, color) {
    const { chartName } = this.props;
    // Above the plot, in the flags row beneath the band (see drawAnnotationAtX).
    const iconY = -14;
    const neutralFill = (this.props.theme && this.props.theme.colors && this.props.theme.colors.greyXXXL) || '#F7F7F7';
    const g = d3.select(this.ctx.chart)
      .append('g')
      .attr('class', `${chartName} annotationIcon`)
      .attr('transform', `translate(${xVal}, ${iconY})`);

    g.append('circle')
      .attr('r', 11)
      .attr('fill', neutralFill)
      .attr('stroke', lightenHex(color || '#1a73e8', 48))
      .attr('stroke-width', 1.5);

    g.append('text')
      .text(count)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('y', 0.5)
      .attr('font-size', 11)
      .attr('fill', lightenHex(color || '#1a73e8', 48))
      .attr('font-weight', 700);
  }


  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyStrokes);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyStrokes);
    // Clean up right-click event listener to prevent memory leaks
    cleanupRightClickEvent(this.ctx);
    if (this.visibleDomainRaf && typeof window !== 'undefined' && window.cancelAnimationFrame) {
      window.cancelAnimationFrame(this.visibleDomainRaf);
      this.visibleDomainRaf = null;
    }
    // Cancel any pending tooltip-hide so its setState can't fire post-unmount.
    if (this.tooltipHideTimer) {
      clearTimeout(this.tooltipHideTimer);
      this.tooltipHideTimer = null;
    }
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.warningThreshold !== this.props.warningThreshold ||
      prevProps.criticalThreshold !== this.props.criticalThreshold ||
      prevProps.baselineValue !== this.props.baselineValue
    ) {
      this.scaleYaxisToShowAlarmLines();
      this.drawAlarmAndBaselineLines();
      this.drawOutliers();
    }
    if (typeof this.props.filterMinVal === 'number' && typeof this.props.filterMaxVal === 'number' && (!_.isEqual(this.props.filterMinVal, prevProps.filterMinVal) || !_.isEqual(this.props.filterMaxVal, prevProps.filterMaxVal))) {
      this.applyFilterGradient(this.ctx);
    } else if ((!_.isEqual(this.props.filterMinVal, prevProps.filterMinVal) || !_.isEqual(this.props.filterMaxVal, prevProps.filterMaxVal))) {
      const { chartName, colors } = this.props;
      const dataline = d3.select(`.${chartName}.dataline.idx-0`);
      dataline.attr('stroke', colors ? colors[0] : '#91B846');
    }
    if (prevProps.editRULSettings !== this.props.editRULSettings || prevProps.rulFdpDatapoint !== this.props.rulFdpDatapoint) this.drawFdp();
    if (prevProps.editRULSettings !== this.props.editRULSettings || prevProps.rulStartTimeDatapoint !== this.props.rulStartTimeDatapoint) this.drawStartTime();

    // Check if selectedPoints array has changed
    if (!_.isEqual(prevProps.selectedPoints, this.props.selectedPoints) && this.ctx) {
      // Redraw points when a point is removed from selectedPoints
      this.drawPoints(this.ctx, this.props.points);
    }

    // Check if annotations array has changed - redraw with new filtered annotations
    if (!_.isEqual(prevProps.annotations, this.props.annotations) && this.ctx) {
      this.drawAnnotations();
    }

    // Spotlight a range's span on the plot when its lane pill (in the
    // RangeLaneBand above the chart) is hovered, mirroring the in-plot hover.
    if (prevProps.highlightAnnotation !== this.props.highlightAnnotation && this.ctx) {
      if (this.props.highlightAnnotation) this.highlightAnnotationRegion(this.props.highlightAnnotation);
      else this.clearAnnotationHighlight();
    }
  }

  drawAnnotations() {
    // Draw vertical dotted lines for point-in-time annotations
    if (!this.ctx) {
      return;
    }

    const { chartName } = this.props;
    // Always remove existing annotations first
    d3.selectAll(`.${chartName}.annotationLine`).remove();
    d3.selectAll(`.${chartName}.annotationIcon`).remove();
    d3.selectAll(`.${chartName}.annotationRange`).remove();
    d3.selectAll(`.${chartName}.annotationRangeTint`).remove();
    d3.selectAll(`.${chartName}.annotationRangeHighlight`).remove();

    // If no annotations to draw, we're done (they've been removed above)
    if (!Array.isArray(this.props.annotations) || !this.props.annotations.length) {
      return;
    }

    // Precompute chart width to avoid repeated layout reads
    const chartWidth = this.ctx.chart.getBoundingClientRect().width;

    // Prepare points with x positions. Anything that is not a range-capable
    // band (see isRangeAnnotation) renders here as a point — including repairs,
    // tasks, diagnostics, and any range-capable type that lacks a distinct
    // end_time. This is the exact inverse of the range filter below so no
    // annotation can fall through both.
    const prepared = this.props.annotations
      .filter(a => a && !isRangeAnnotation(a))
      .map((a) => {
        const t = new Date(a.start_time).getTime();
        if (Number.isNaN(t)) return null;
        const xVal = this.ctx.x(t);
        if (xVal < 0 || xVal > chartWidth) return null;
        const color = getAnnotationColor(a, { respectSeverity: false });
        // for grouping interface compatibility
        return { ref: a, a, xVal, xPx: xVal, xPercent: (xVal / chartWidth) * 100, yPercent: 50, color };
      })
      .filter(Boolean);

    // Group by horizontal proximity
    const grouped = groupByProximity(prepared, chartWidth);

    // Draw clusters or individual annotations
    grouped.forEach((group) => {
      if (group.items.length === 1) {
        const { a, xPx: xVal, color } = group.items[0];
        const line = graphUtils.drawLineOnPos(
          this.ctx.chart,
          xVal,
          0,
          xVal,
          this.ctx.chart.getBoundingClientRect().height,
          `${chartName} annotationLine`
        );
        line.attr('stroke', color).attr('stroke-dasharray', '3,3').attr('opacity', 0.35);
        this.drawAnnotationAtX(a, xVal, color);
        return;
      }

      // cluster: draw one line at the average x, fan-out up to 4 icons, then a +N bubble
      const xAvg = (group.xPercent / 100) * chartWidth;
      const lineColor = group.items[0].color;
      const line = graphUtils.drawLineOnPos(
        this.ctx.chart,
        xAvg,
        0,
        xAvg,
        this.ctx.chart.getBoundingClientRect().height,
        `${chartName} annotationLine`
      );
      line.attr('stroke', lineColor).attr('stroke-dasharray', '3,3').attr('opacity', 0.35);

      const fan = Math.min(4, group.items.length);
      const spread = 10; // px offset between fanned icons
      const startOffset = -((fan - 1) / 2) * spread;
      for (let i = 0; i < fan; i += 1) {
        const { a, color } = group.items[i];
        const x = xAvg + startOffset + (i * spread);
        this.drawAnnotationAtX(a, x, color);
      }
      if (group.items.length > fan) {
        const remaining = group.items.length - fan;
        this.drawClusterBubbleAtX(`+${remaining}`, xAvg + startOffset + (fan * spread), lineColor);
      }
    });

    // Time-range annotations (alarm/note lanes) are no longer drawn inside the
    // plot — they render in the standalone RangeLaneBand strip above the chart
    // (see AnnotatedChart). Only the point-in-time flags above stay in-plot.
  }

  // Highlight a single annotation's time span on the trend (full-height band),
  // used when hovering its row in the cluster popover.
  highlightAnnotationRegion(annotation) {
    if (!this.ctx || !annotation) return;
    const { chartName } = this.props;
    d3.selectAll(`.${chartName}.annotationRangeHighlight`).remove();

    const ts = new Date(annotation.start_time).getTime();
    const te = new Date(annotation.end_time || annotation.start_time).getTime();
    if (Number.isNaN(ts)) return;

    const rect = this.ctx.chart.getBoundingClientRect();
    const x0 = Math.max(0, Math.min(rect.width, this.ctx.x(Math.min(ts, te))));
    const x1 = Math.max(0, Math.min(rect.width, this.ctx.x(Math.max(ts, te))));
    const width = Math.max(2, x1 - x0);
    const color = getAnnotationColor(annotation, { respectSeverity: false });

    d3.select(this.ctx.chart)
      .insert('rect', ':first-child')
      .attr('class', `${chartName} annotationRangeHighlight`)
      .attr('x', x0)
      .attr('y', 0)
      .attr('width', width)
      .attr('height', rect.height)
      .attr('fill', color)
      .attr('opacity', 0.22)
      .attr('stroke', color)
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', 1)
      .attr('pointer-events', 'none');
  }

  clearAnnotationHighlight() {
    if (!this.ctx) return;
    const { chartName } = this.props;
    d3.selectAll(`.${chartName}.annotationRangeHighlight`).remove();
  }

  drawGraph(ctx) {
    this.ctx = ctx;

    this.scaleYaxisToShowAlarmLines();
    this.drawAlarmAndBaselineLines();
    this.drawOutliers();
    this.drawFdp();
    this.drawStartTime();

    // Draw annotations
    this.drawAnnotations();

    // Use shared right-click utility
    addOutlierRightClickEvent(ctx, {
      enableRightClick: true,
      miniMode: this.props.miniMode,
      component: 'trend',
      onRightClick: (dataPos, selection) => {
        if (this.props.setDataPosAndDataPoint) {
          this.props.setDataPosAndDataPoint(dataPos, selection);
        }
      }
    });

    this.emitVisibleDomain();
  }

  redraw(ctx) {
    this.ctx = ctx;

    this.drawAlarmAndBaselineLines();
    this.drawOutliers();
    this.drawFdp();
    this.drawStartTime();

    // Redraw annotation lines on zoom/pan
    this.drawAnnotations();

    this.emitVisibleDomain();
  }

  // Report the plot's current visible x-domain so the range-lane band rendered
  // above the chart (AnnotatedChart → RangeLaneBand) can stay aligned through
  // zoom/pan/brush. Called on every draw/redraw — i.e. on every frame of a
  // continuous zoom/pan — so it is coalesced to at most one emit per animation
  // frame to avoid a re-render storm in the band. The listener also de-dupes.
  emitVisibleDomain() {
    if (typeof this.props.onVisibleDomainChange !== 'function') return;
    if (this.visibleDomainRaf) return; // an emit is already scheduled this frame
    const raf = (typeof window !== 'undefined' && window.requestAnimationFrame)
      ? window.requestAnimationFrame.bind(window)
      : (cb => cb());
    this.visibleDomainRaf = raf(() => {
      this.visibleDomainRaf = null;
      if (!this.ctx || !this.ctx.x || typeof this.props.onVisibleDomainChange !== 'function') return;
      const d = this.ctx.x.domain();
      if (!d || d.length < 2) return;
      const start = d[0] instanceof Date ? d[0].getTime() : new Date(d[0]).getTime();
      const end = d[1] instanceof Date ? d[1].getTime() : new Date(d[1]).getTime();
      if (Number.isNaN(start) || Number.isNaN(end)) return;
      this.props.onVisibleDomainChange({ start, end });
    });
  }

  showAnnotationTooltip(annotation, xVal) {
    if (!this.ctx || !this.ctx.chart) return;
    const rect = this.ctx.chart.getBoundingClientRect();
    const margin = 12;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const tooltipWidth = 360; // matches TooltipContainer max-width
    const approxTooltipHeight = 260; // estimate to avoid offscreen

    const baseLeft = rect.left + xVal;

    // Horizontal alignment and clamping
    let align = 'center';
    let x = baseLeft;
    if (baseLeft < margin + tooltipWidth / 2) {
      align = 'left';
      x = Math.max(margin, baseLeft);
    } else if (baseLeft > viewportWidth - (margin + tooltipWidth / 2)) {
      align = 'right';
      x = Math.min(viewportWidth - margin, baseLeft);
    }

    // Always show under the icon (below chart top), with bottom placement
    const placement = 'bottom';
    let y = rect.top + 10;
    if (y + approxTooltipHeight > viewportHeight - margin) {
      // If it would go off-screen at bottom, clamp near bottom
      y = Math.max(margin, viewportHeight - approxTooltipHeight - margin);
    }

    this.lastXVal = xVal; // store for tooltip mouseenter recalculation

    if (this.tooltipHideTimer) {
      clearTimeout(this.tooltipHideTimer);
      this.tooltipHideTimer = null;
    }

    this.setState({
      showAnnotationTooltip: true,
      tooltipAnnotation: annotation,
      tooltipPos: { x, y, placement, align }
    });
  }

  scheduleHideAnnotationTooltip(delay = 200) {
    if (this.tooltipHideTimer) clearTimeout(this.tooltipHideTimer);
    this.tooltipHideTimer = setTimeout(() => {
      this.setState({ showAnnotationTooltip: false });
      this.tooltipHideTimer = null;
    }, delay);
  }

  hideAnnotationTooltip() {
    if (this.tooltipHideTimer) {
      clearTimeout(this.tooltipHideTimer);
      this.tooltipHideTimer = null;
    }
    this.setState({ showAnnotationTooltip: false });
  }

  redrawDataLines = (x, y, ctx) => {
    this.props.data.forEach((dataSet, idx) => {
      if (this.props.activeData[idx]) {
        if (!ctx || !ctx.chartDatalines || !ctx.chartDatalines[idx]) return;
        const dataline = ctx.chartDatalines[idx]
          .attr('d', d3.line()
            .curve(d3.curveLinear)
            .x(d => x(d.x))
            .y(d => y(d.y)));
        dataline.attr('stroke', this.props.colors ? this.props.colors[idx] : '#91B846');
        this.applyValidRangeGradient(ctx);

        if (!this.props.filterMinVal || !this.props.filterMaxVal) return;
        this.applyFilterGradient(ctx);
      }
    });
  };

  applyFilterGradient = (ctx) => {
    const { filterMinVal, filterMaxVal, filterKey } = this.props;
    const { chartName } = this.props;
    const colorCodes = {
      green: '#91B846',
      grey: '#d8dcd3'
    };
    let currentColor = 'green';
    const data = this.props.data[0];
    const xDomain = ctx.x.domain();
    const xRange = ctx.x.range();
    const gradientArray = [];
    data.forEach((cur, idx) => {
      if (cur.x < xDomain[0] || cur.x > xDomain[1]) return;
      if (idx === 0) {
        if (cur[filterKey] >= filterMinVal && cur[filterKey] <= filterMaxVal) {
          gradientArray.push({
            offset: '0%', color: colorCodes.green
          });
        } else {
          gradientArray.push({
            offset: '0%', color: colorCodes.grey
          });
          currentColor = 'grey';
        }
      } else if (cur[filterKey] >= filterMinVal && cur[filterKey] <= filterMaxVal && currentColor !== 'green') {
        const curXOffset = ctx.x(cur.x);
        const prevXOffset = ctx.x(data[idx - 1].x);
        const xOffset = ((curXOffset + prevXOffset) / 2) / (xRange[1] - xRange[0]) * 100;
        gradientArray.push({
          offset: `${xOffset}%`, color: colorCodes.grey
        }, {
          offset: `${xOffset}%`, color: colorCodes.green
        });
        currentColor = 'green';
      } else if ((cur[filterKey] < filterMinVal || cur[filterKey] > filterMaxVal) && currentColor !== 'grey') {
        const curXOffset = ctx.x(cur.x);
        const prevXOffset = ctx.x(data[idx - 1].x);
        const xOffset = ((curXOffset + prevXOffset) / 2) / (xRange[1] - xRange[0]) * 100;
        gradientArray.push({
          offset: `${xOffset}%`, color: colorCodes.green
        }, {
          offset: `${xOffset}%`, color: colorCodes.grey
        });
        currentColor = 'grey';
      }
    });
    d3.select(`defs.${chartName}`).select('#line-gradient').remove();
    d3.select(`defs.${chartName}`)
      .append('linearGradient')
      .attr('id', 'line-gradient')
      .attr('class', `${chartName}`)
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', xRange[1])
      .attr('y2', 0)
      .selectAll('stop')
      .data(gradientArray)
      .enter()
      .append('stop')
      .attr('offset', d => d.offset)
      .attr('stop-color', d => d.color);
  };

  applyValidRangeGradient = (ctx) => {
    const { color } = this.props;

    if (!this.props.enableOnOffIndicator) return;
    const data = this.props.data[0];

    const colors = {
      default: color,
      invalid: '#d8dcd3',
    };
    const { chartName } = this.props;
    const gradientArray = [];
    const xDomain = ctx.x.domain();
    const xRange = ctx.x.range();

    data.forEach((cur, idx) => {
      if (cur.x < xDomain[0] || cur.x > xDomain[1]) return;
      const prev = Math.max(idx - 1, 0);

      const curXOffset = ctx.x(cur.x);
      const prevXOffset = ctx.x(data[prev].x);
      const xOffset = ((curXOffset + prevXOffset) / 2) / (xRange[1] - xRange[0]) * 100;

      gradientArray.push({
        offset: `${xOffset}%`, color: Number(cur.on_off_reading) === 1 ? colors.default : colors.invalid
      });
    });
    d3.select(`defs.${chartName}`).select('#line-gradient').remove();
    d3.select(`defs.${chartName}`)
      .append('linearGradient')
      .attr('id', 'line-gradient')
      .attr('class', `${chartName}`)
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', xRange[1])
      .attr('y2', 0)
      .selectAll('stop')
      .data(gradientArray)
      .enter()
      .append('stop')
      .attr('offset', d => d.offset)
      .attr('stop-color', d => d.color);
  }

  handleKeyStrokes = (e) => {
    if (!this.ctx || !this.ctx.chart || !this.ctx.x || this.props.lastHoveredChart !== this.props.type) return;
    if (e.keyCode !== KEYBOARD_CODES.LEFT_ARROW && e.keyCode !== KEYBOARD_CODES.RIGHT_ARROW && e.keyCode !== KEYBOARD_CODES.ENTER) return;

    const { waveAndSpecSelection, data, selection } = _.cloneDeep(this.props);
    const isVisible = isElementPartiallyVisible(this.ctx.chart);

    if (!waveAndSpecSelection || !isVisible || !data.length) return;

    let idx = data[0].findIndex(d => d.measurement_id === selection.measurement_id);
    if (idx === -1) return;

    const minIndex = 0;
    const maxIndex = data[0].length - 1;
    switch (e.keyCode) {
      case KEYBOARD_CODES.LEFT_ARROW:
        idx -= 1;
        if (idx < minIndex) return;

        // some entries of data randomly have dataIdx key (BUG)
        if (selection.dataIdx) delete selection.dataIdx;

        this.ctx.props.mouseover(_.cloneDeep(data[0][idx], this.ctx.props.chartName));
        break;

      case KEYBOARD_CODES.RIGHT_ARROW:
        idx += 1;
        if (idx > maxIndex) return;
        if (selection.dataIdx) delete selection.dataIdx;

        this.ctx.props.mouseover(_.cloneDeep(data[0][idx], this.ctx.props.chartName));
        break;

      case KEYBOARD_CODES.ENTER:
        this.props.mouseClick(_.cloneDeep(data[0][idx]));
        break;

      default:
        break;
    }
  };

  scaleYaxisToShowAlarmLines() {
    const { criticalThreshold, baselineValue } = this.props;

    let lines = [criticalThreshold];
    if (this.props.showBaseline) {
      lines = [criticalThreshold, baselineValue];
    }
    const maxLineValue = Math.max(...lines.filter(l => l));

    if (
      maxLineValue &&
      (this.props.enableAlarmAndBaselineLines || parseInt(this.props.height, 10) >= 300) &&
      this.props.range[1] - maxLineValue < 0.1 * maxLineValue
    ) {
      this.ctx.y.domain([this.props.range[0], 1.1 * maxLineValue]);
      this.ctx.y2.domain([this.props.range[0], 1.1 * maxLineValue]);
      this.ctx.drawAxis();
      this.ctx.drawDataLines();
      if (this.props.contextChart) this.ctx.enableZoom();
    }
  }

  drawOutliers() {
    const { outliersArray, chartName, height } = this.props;
    d3.selectAll(`.${chartName}.outliers`).remove();
    if (this.ctx && outliersArray) {
      outliersArray.forEach((d) => {
        if (this.ctx.x(d.x) > 0 && this.ctx.x(d.x) < this.ctx.chart.getBoundingClientRect().width
              && this.ctx.y(d.y) > 0 && this.ctx.y(d.y) < parseInt(height, 10)) {
          graphUtils.drawCircleOnPos(this.ctx.chart, this.ctx.x(d.x), this.ctx.y(d.y), 2.5, `${chartName} outliers`).attr('fill', 'gray');
        }
      });
    }
  }

  drawFdp() {
    const { chartName, editRULSettings, rulFdpDatapoint } = this.props;
    d3.selectAll(`.${chartName}.fdp`).remove();
    if (editRULSettings && this.ctx && !_.isEmpty(rulFdpDatapoint)) {
      graphUtils.drawCircleOnPos(this.ctx.chart, this.ctx.x(rulFdpDatapoint.x), this.ctx.y(rulFdpDatapoint.y), 2.5, `${chartName} fdp`).attr('fill', 'blue');
      graphUtils.drawLineOnPos(this.ctx.chart, this.ctx.x(rulFdpDatapoint.x), 0, this.ctx.x(rulFdpDatapoint.x), this.ctx.chart.getBoundingClientRect().height, `${chartName} fdp`).attr('stroke', 'blue');
    }
  }

  drawStartTime() {
    const { chartName, editRULSettings, rulStartTimeDatapoint } = this.props;
    d3.selectAll(`.${chartName}.startTime`).remove();
    if (editRULSettings && this.ctx && !_.isEmpty(rulStartTimeDatapoint)) {
      graphUtils.drawCircleOnPos(this.ctx.chart, this.ctx.x(rulStartTimeDatapoint.x), this.ctx.y(rulStartTimeDatapoint.y), 2.5, `${chartName} startTime`).attr('fill', 'green');
      graphUtils.drawLineOnPos(this.ctx.chart, this.ctx.x(rulStartTimeDatapoint.x), 0, this.ctx.x(rulStartTimeDatapoint.x), this.ctx.chart.getBoundingClientRect().height, `${chartName} startTime`).attr('stroke', 'green');
    }
  }

  drawAlarmAndBaselineLines() {
    const { warningThreshold, criticalThreshold, baselineValue } = this.props;
    const chartHeight = parseInt(this.props.height, 10);
    if (!this.props.enableAlarmAndBaselineLines && chartHeight < 300) {
      d3.selectAll(`.${this.props.chartName}.warning`).remove();
      d3.selectAll(`.${this.props.chartName}.critical`).remove();
      d3.selectAll(`.${this.props.chartName}.baseline`).remove();
      return;
    }

    // warning section
    if (this.props.tag_type === 'vibration' && ((this.props.ampType !== 'velocity' && this.props.ampType !== 'demod' && this.props.ampType !== 'acceleration') || this.props.feature === 'crest_factor')) {
      d3.selectAll(`.${this.props.chartName}.warning`).remove();
      d3.selectAll(`.${this.props.chartName}.critical`).remove();
      return;
    }

    d3.selectAll(`.${this.props.chartName}.warning`).remove();
    if (warningThreshold !== undefined && warningThreshold !== null) {
      if (this.ctx.y(warningThreshold) > 0 && this.ctx.y(warningThreshold) < chartHeight) {
        this.warningLine = graphUtils.drawLineOnPos(
          this.ctx.chart,
          0,
          this.ctx.y(warningThreshold),
          this.ctx.chart.getBoundingClientRect().width,
          this.ctx.y(warningThreshold),
          `${this.props.chartName} warning`
        );
        this.warningLine.attr('stroke', this.props.theme.colors.alarmWarning);
      }
    }

    // critical section

    d3.selectAll(`.${this.props.chartName}.critical`).remove();
    if (criticalThreshold !== undefined && criticalThreshold !== null) {
      if (this.ctx.y(criticalThreshold) > 0 && this.ctx.y(criticalThreshold) < chartHeight) {
        this.baselineLine = graphUtils.drawLineOnPos(
          this.ctx.chart,
          0,
          this.ctx.y(criticalThreshold),
          this.ctx.chart.getBoundingClientRect().width,
          this.ctx.y(criticalThreshold),
          `${this.props.chartName} critical`
        );
        this.baselineLine.attr('stroke', this.props.theme.colors.alarmCritical);
      }
    }


    // baseline value section

    d3.selectAll(`.${this.props.chartName}.baseline`).remove();
    if (this.props.showBaseline && baselineValue !== undefined && baselineValue !== null) {
      if (this.ctx.y(baselineValue) > 0 && this.ctx.y(baselineValue) < chartHeight) {
        this.criticalLine = graphUtils.drawLineOnPos(
          this.ctx.chart,
          0,
          this.ctx.y(baselineValue),
          this.ctx.chart.getBoundingClientRect().width,
          this.ctx.y(baselineValue),
          `${this.props.chartName} baseline`
        );
        this.criticalLine.attr('stroke', this.props.theme.colors.baselineLine);
      }
    }
  }

  drawPoints(ctx, points) {
    d3.selectAll(`.${ctx.props.chartName}.pointSelection`).remove();

    // Check if we're in overlay mode and have an array of selected points
    if (this.props.showOverlay && Array.isArray(this.props.selectedPoints) && this.props.selectedPoints.length > 0) {
      // Draw multiple points for overlay mode
      this.props.selectedPoints.forEach((point, index) => {
        if (!point || _.isEmpty(point)) return;

        // Use point's assigned color if available, otherwise use overlayColors
        const color = point.color || overlayColors[index % overlayColors.length];

        // In overlay mode we mark each selected reading with a colored dot only.
        // A vertical guide line per point (as used for the single-point selection
        // below) would stack up into a wall of lines across the trend, so it is
        // intentionally not drawn here.

        // Create circle with dynamic fill color
        const circle = graphUtils.drawCircleOnPos(
          ctx.chart,
          ctx.x(point.x),
          ctx.y(point.y),
          4,
          `${ctx.props.chartName} pointSelection`
        );

        // Set fill color dynamically while keeping border
        circle
          .attr('fill', color)
          .style('fill', color)
          .attr('stroke', '#000000')
          .attr('stroke-width', '2px');
      });

      // If we have multiple points and the last one is the most recently clicked,
      // we should also update data position and point for tooltips
      if (this.props.setDataPosAndDataPoint && this.props.selectedPoints.length > 0) {
        const lastPoint = this.props.selectedPoints[this.props.selectedPoints.length - 1];
        let dataPos = {
          x: false,
          y: false
        };

        if (ctx.x(lastPoint.x) >= 0 && ctx.x(lastPoint.x) <= ctx.chart.getBoundingClientRect().width
          && ctx.y(lastPoint.y) >= 0 && ctx.y(lastPoint.y) <= parseInt(ctx.props.height, 10)) {
          dataPos = {
            x: ctx.x(lastPoint.x),
            y: ctx.y(lastPoint.y)
          };

          if (dataPos.x > ctx.chart.getBoundingClientRect().width - 250) {
            dataPos.alignLeft = true;
          }
        }

        this.props.setDataPosAndDataPoint(dataPos, lastPoint);
      }
    } else {
      // Default functionality for single point
      const dataPoint = this.props.waveAndSpecSelection || this.props.points;
      if (_.isEmpty(dataPoint)) return;

      graphUtils.drawLineOnPos(
        ctx.chart,
        ctx.x(dataPoint.x),
        0,
        ctx.x(dataPoint.x),
        ctx.chart.getBoundingClientRect().height,
        `${ctx.props.chartName} pointSelection`
      );

      // Create circle with dynamic fill color for single point
      const circle = graphUtils.drawCircleOnPos(
        ctx.chart,
        ctx.x(dataPoint.x),
        ctx.y(dataPoint.y),
        4,
        `${ctx.props.chartName} pointSelection`
      );

      // Force white color for single point mode
      circle
        .attr('fill', '#FFFFFF')
        .style('fill', '#FFFFFF')
        .attr('stroke', '#000000')
        .attr('stroke-width', '2px');
    }
  }


  render() {
    const {
      showAnnotationTooltip, tooltipAnnotation, tooltipPos
    } = this.state;
    return (
      <>
        {showAnnotationTooltip && tooltipAnnotation && (
          <AnnotationTooltip
            annotation={tooltipAnnotation}
            position={tooltipPos}
            showControls
            onClose={() => this.scheduleHideAnnotationTooltip(0)}
            onMouseEnter={() => this.showAnnotationTooltip(tooltipAnnotation, this.lastXVal || 0)}
            onMouseLeave={() => this.scheduleHideAnnotationTooltip(150)}
            onEdit={this.props.onAnnotationClick}
          />
        )}
      </>
    );
  }
}

TrendChart.displayName = 'TrendChart';

export default withTheme(LineChart(TrendChart));
